import { useState, useEffect, useRef, useCallback } from 'react'
import { generateClientUniqueId } from '@klear/sse'

const globalSSERefs = new Map<
  string,
  {
    eventSource: EventSource | null
    connectionAttempted: boolean
    componentCount: number
  }
>()

export function useEventSource<T = unknown>(
  url: string,
  options?: {
    connect?: boolean
    messageType?: string
    onMessage?: (data: T) => void
    onError?: (error: Event) => void
    onOpen?: () => void
  }
) {
  const [data, setData] = useState<T | null>(null)
  const [error, setError] = useState<Event | null>(null)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [isConnected, setIsConnected] = useState<boolean>(false)

  const mountCountRef = useRef(0)
  const componentIdRef = useRef(`${url}-${generateClientUniqueId()}`)

  const callbacksRef = useRef({
    onMessage: options?.onMessage,
    onError: options?.onError,
    onOpen: options?.onOpen,
    messageType: options?.messageType
  })

  useEffect(() => {
    callbacksRef.current = {
      onMessage: options?.onMessage,
      onError: options?.onError,
      onOpen: options?.onOpen,
      messageType: options?.messageType
    }
  }, [options?.onMessage, options?.onError, options?.onOpen, options?.messageType])

  const getGlobalSSERef = useCallback(() => {
    if (!globalSSERefs.has(url)) {
      globalSSERefs.set(url, {
        eventSource: null,
        connectionAttempted: false,
        componentCount: 0
      })
    }
    return globalSSERefs.get(url)!
  }, [url])

  useEffect(() => {
    return () => {
      const globalRef = getGlobalSSERef()
      globalRef.componentCount = Math.max(0, globalRef.componentCount - 1)

      if (globalRef.componentCount === 0 && globalRef.eventSource) {
        globalRef.eventSource.close()
        globalRef.eventSource = null
        globalRef.connectionAttempted = false
      }
    }
  }, [url, getGlobalSSERef])

  useEffect(() => {
    if (options?.connect === false) {
      return
    }
    mountCountRef.current++
    const globalRef = getGlobalSSERef()
    globalRef.componentCount++
  }, [url, getGlobalSSERef, options?.connect])

  useEffect(() => {
    if (!url || options?.connect === false) {
      return
    }
    const globalRef = getGlobalSSERef()

    if (globalRef.eventSource || globalRef.connectionAttempted) {
      return
    }

    globalRef.connectionAttempted = true
    setIsLoading(true)

    try {
      globalRef.eventSource = new EventSource(url, { withCredentials: true })

      globalRef.eventSource.onopen = () => {
        setIsLoading(false)
        setIsConnected(true)
        callbacksRef.current.onOpen?.()
      }

      globalRef.eventSource.onmessage = (event) => {
        try {
          const parsedData = JSON.parse(event.data)

          if (callbacksRef.current.messageType && parsedData.type !== callbacksRef.current.messageType) {
            return
          }

          setData(parsedData)
          setError(null)
          callbacksRef.current.onMessage?.(parsedData)
        } catch (error) {
          console.error('Error parsing SSE message:', error)
          setError(error as Event)
          callbacksRef.current.onError?.(error as Event)
        }
      }

      if (callbacksRef.current.messageType) {
        globalRef.eventSource.addEventListener(callbacksRef.current.messageType, (event: MessageEvent) => {
          try {
            const parsedData = JSON.parse(event.data)
            setData(parsedData)
            setError(null)
            callbacksRef.current.onMessage?.(parsedData)
          } catch (error) {
            console.error(`Error parsing SSE ${callbacksRef.current.messageType} event:`, error)
            setError(error as Event)
            callbacksRef.current.onError?.(error as Event)
          }
        })
      }

      globalRef.eventSource.onerror = (error) => {
        console.error('SSE connection error:', error)
        setError(error)
        setIsConnected(false)
        callbacksRef.current.onError?.(error)

        if (globalRef.eventSource) {
          globalRef.eventSource.close()
          globalRef.eventSource = null
          globalRef.connectionAttempted = false
        }
      }
    } catch (error) {
      console.error('Error setting up SSE connection:', error)
      setError(error as Event)
      setIsLoading(false)
      globalRef.connectionAttempted = false
    }
  }, [url, getGlobalSSERef])

  const reconnect = () => {
    const globalRef = getGlobalSSERef()
    if (globalRef.eventSource) {
      globalRef.eventSource.close()
      globalRef.eventSource = null
    }
    globalRef.connectionAttempted = false
    setError(null)
    setIsLoading(false)
    setIsConnected(false)
  }

  return {
    data,
    error,
    isLoading,
    isConnected,
    reconnect,
    _debug: {
      componentId: componentIdRef.current,
      mountCount: mountCountRef.current,
      globalRef: getGlobalSSERef()
    }
  }
}
